import { useState, useEffect, useCallback, useMemo } from 'react';
import { McParameterDetailResponseDto } from 'src/api/references/referenceServicesDtos';
import { getCardData, BaseCardData } from 'src/utils/card/cardData';
import { referenceService } from 'src/api/references/referenceService';
import { useTranslate } from 'src/locales/use-locales';
import { useMcpCache } from './useMcpCache';
import { useMedicalCardLoading } from './useMedicalCardLoading';

interface UseCardFormProps {
  cardType: string;
  recordId?: string;
  user?: any;
  onLoadRecord?: (record: any) => void;
}

//476622 
//useCardForm hook'u eklendi alanları yükleme, stateleri set etme ve formu resetlemeyi yapıyor ayrıca gettablename vb. 
// gibi ek fonskiyonlara sahip bunları cardData üzerinden çekiyor. CardData da artık mcpConfig alanı var mcp alanların 
// id'leri değer adı ve değerin value alanı burada belirleniyor mcp ile ilgili selectleri useCardForm böylece 
// yönetebiliyor, glassgowCreate bu hooku kullanacak şekilde güncellendi. CardList güncellendi.


export const useCardForm = ({ cardType, recordId, user, onLoadRecord }: UseCardFormProps) => {
  const { t } = useTranslate();
  const cardData = getCardData(cardType) as BaseCardData;
  const { setIsLoading: setGlobalLoading } = useMedicalCardLoading();

  if (!cardData) {
    throw new Error(`Card data not found for type: ${cardType}`);
  }

  const [formData, setFormData] = useState(cardData.formData);
  const [selectedMCPs, setSelectedMCPs] = useState<Record<string, McParameterDetailResponseDto | null>>({});
  const [loading, setLoading] = useState(false);

  const mcpIds = useMemo(() => {
    const configIds = cardData?.mcpConfig ? Object.values(cardData.mcpConfig).map((config: any) => config.mcpId) : [];
    const extraIds = cardData?.extraMcpIds || [];
    return Array.from(new Set([...configIds, ...extraIds]));
  }, [cardData]);

  const {
    cacheLoaded: mcpCacheLoaded,
    loading: mcpCacheLoading,
    error: mcpCacheError,
    setError: setMcpCacheError,
    getMcpParameterDetails
  } = useMcpCache(mcpIds);

  const [error, setError] = useState<{ message: string; open: boolean } | null>(null);

  const isEditMode = !!recordId;

  const handleChange = useCallback((field: string, value: any) => {
    setFormData((prev: any) => {
      if (prev[field] === value) return prev;
      return {
        ...prev,
        [field]: value
      };
    });
  }, []);

  const handleMCPChange = useCallback((mcpName: string, value: McParameterDetailResponseDto | null) => {
    if (!cardData?.mcpConfig) return;

    const mcpConfig = cardData.mcpConfig[mcpName as keyof typeof cardData.mcpConfig];
    if (!mcpConfig) return;

    setSelectedMCPs(prev => {
      if (prev[mcpName] === value) return prev;
      return {
        ...prev,
        [mcpName]: value
      };
    });

    if (value) {
      if (mcpConfig.field === mcpConfig.scoreField) {
        handleChange(mcpConfig.field, value.id);
      } else {
        handleChange(mcpConfig.field, value.id);
        handleChange(mcpConfig.scoreField, value.value);
      }
    } else {
      if (mcpConfig.field === mcpConfig.scoreField) {
        handleChange(mcpConfig.field, "");
      } else {
        handleChange(mcpConfig.field, "");
        handleChange(mcpConfig.scoreField, "");
      }
    }
  }, [cardData, handleChange]);


  const loadRecord = useCallback(async () => {
    if (!isEditMode || !recordId || !cardData) return;

    try {
      setLoading(true);
      setGlobalLoading(true);
      const tableName = cardData.tableName;
      if (!tableName) return;

      if (tableName == 'PATIENT_VITAL_RECORDS')
        return;

      const response = await referenceService.card.getCardTableData({
        tableName,
        columnName: "ID",
        columnValue: parseInt(recordId),
        mcpdNameList: Object.keys(cardData.mcpConfig || {}).join(",")
      });

      if (response.data && response.data.length > 0) {
        const record = response.data[0] as any;

        const isDateField = (key: string) => {
          if (["PROCESS_TIME", "CTIME", "DATE_BIRTH"].includes(key)) return true;
          const fieldConfig = cardData.fields?.find((f: any) => f.field === key) as any;
          if (fieldConfig?.type?.toLowerCase() === "date") return true;
          const rowConfig = cardData.rows?.find((r: any) => r.value === key) as any;
          if (rowConfig?.type?.toLowerCase() === "date") return true;
          return false;
        };

        if (record.columns && Array.isArray(record.columns)) {
          record.columns.forEach((column: { key: string; value: any }) => {
            if (isDateField(column.key)) {
              if (column.value) {
                handleChange(column.key, new Date(column.value as string));
              } else if (["PROCESS_TIME", "CTIME"].includes(column.key)) {
                handleChange(column.key, new Date());
              } else {
                handleChange(column.key, null);
              }
            } else {
              handleChange(column.key, column.value?.toString() || "");
            }
          });
        } else {
          Object.entries(record).forEach(([key, value]) => {
            if (isDateField(key)) {
              if (value) {
                handleChange(key, new Date(value as string));
              } else if (["PROCESS_TIME", "CTIME"].includes(key)) {
                handleChange(key, new Date());
              } else {
                handleChange(key, null);
              }
            } else {
              handleChange(key, value?.toString() || "");
            }
          });
        }

        const mcpConfigs = cardData.mcpConfig;
        if (mcpConfigs) {
          const formDataUpdates: Record<string, any> = {};

          Object.entries(mcpConfigs).forEach(([mcpName, config]: [string, any]) => {
            let fieldValue: any;
            let scoreValue: number | string | undefined;

            if (record.columns && Array.isArray(record.columns)) {
              const fieldColumn = record.columns.find((col: any) => col.key === config.field);
              const scoreColumn = record.columns.find((col: any) => col.key === config.scoreField);
              fieldValue = fieldColumn?.value;
              scoreValue = scoreColumn?.value;
            } else {
              fieldValue = record[config.field];
              scoreValue = record[config.scoreField];
            }

            if (fieldValue) {
              const mcpDetails = getMcpParameterDetails(config.mcpId);
              const foundDetail = mcpDetails.find(detail => detail.id.toString() === String(fieldValue));

              const selectedMCP: McParameterDetailResponseDto = {
                id: String(fieldValue),
                mcpId: config.mcpId,
                name: foundDetail?.name || "",
                note: null,
                value: foundDetail?.value || Number(scoreValue) || 0,
                orderNumber: null
              };

              handleMCPChange(mcpName, selectedMCP);

              if (config.field === config.scoreField) {
                formDataUpdates[config.field] = selectedMCP.id;
              } else {
                formDataUpdates[config.field] = selectedMCP.id;
                formDataUpdates[config.scoreField] = selectedMCP.value;
              }
            } else {
              handleMCPChange(mcpName, null);
              if (config.field === config.scoreField) {
                formDataUpdates[config.field] = "";
              } else {
                formDataUpdates[config.field] = "";
                formDataUpdates[config.scoreField] = "";
              }
            }
          });
        } else {
        }
        if (onLoadRecord) {
          onLoadRecord(record);
        }
      }
    } catch (error) {
      setError({ message: t("error-loading-record"), open: true });
    } finally {
      setLoading(false);
      setGlobalLoading(false);
    }
  }, [recordId, isEditMode]);

  const resetForm = useCallback(() => {
    const initialFormData = {
      ...cardData?.formData,
      CTIME: new Date(),
      PROCESS_TIME: new Date(),
      CUSER: user?.username ?? "",
      NURSE_ID: user?.id?.toString() ?? "",
    };

    setFormData(initialFormData);
    const initialSelectedMCPs: Record<string, McParameterDetailResponseDto | null> = {};
    if (cardData?.mcpConfig) {
      Object.keys(cardData.mcpConfig).forEach(key => {
        initialSelectedMCPs[key] = null;
      });
    }
    setSelectedMCPs(initialSelectedMCPs);
    setError(null);
  }, [cardData, user]);

  const initializeNewRecord = useCallback(() => {
    if (!cardData) return;

    const newFormData = {
      ...cardData.formData,
      CTIME: new Date(),
      PROCESS_TIME: new Date(),
      CUSER: user?.username ?? "",
      NURSE_ID: user?.id?.toString() ?? "",
    };

    setFormData(newFormData);
    const initialSelectedMCPs: Record<string, McParameterDetailResponseDto | null> = {};
    if (cardData?.mcpConfig) {
      Object.keys(cardData.mcpConfig).forEach(key => {
        initialSelectedMCPs[key] = null;
      });
    }
    setSelectedMCPs(initialSelectedMCPs);
    setError(null);
  }, [cardData, user]);

  const getMCPId = useCallback((mcpName: string): number | null => {
    return cardData?.mcpConfig?.[mcpName as keyof typeof cardData.mcpConfig]?.mcpId || null;
  }, [cardData]);

  const getMCPConfig = useCallback((mcpName: string) => {
    return cardData?.mcpConfig?.[mcpName as keyof typeof cardData.mcpConfig];
  }, [cardData]);

  const getAllMCPConfigs = useCallback(() => {
    return cardData?.mcpConfig || {};
  }, [cardData]);

  const getTableName = useCallback(() => cardData.tableName, [cardData]);
  const getMCPNameList = useCallback(() => Object.keys(cardData.mcpConfig || {}), [cardData]);

  const getMCPParameterDetails = getMcpParameterDetails;

  // selectedMCP.id'yi kullanarak gerçek skor değerini cache'den alan merkezi fonksiyon
  const getScoreValue = useCallback((mcpName: string): number => {
    const selectedMCP = selectedMCPs[mcpName];
    if (!selectedMCP || !selectedMCP.id) return 0;

    const mcpConfig = cardData?.mcpConfig?.[mcpName as keyof typeof cardData.mcpConfig];
    if (!mcpConfig) return 0;

    const mcpDetails = getMCPParameterDetails(mcpConfig.mcpId);
    const foundDetail = mcpDetails.find(detail => detail.id.toString() === String(selectedMCP.id));
    return foundDetail?.value || 0;
  }, [selectedMCPs, cardData, getMCPParameterDetails]);

  useEffect(() => {
    setGlobalLoading(loading || mcpCacheLoading);
  }, [loading, mcpCacheLoading, setGlobalLoading]);

  useEffect(() => {
    if (isEditMode && recordId) {
      loadRecord();
    } else if (!isEditMode) {
      initializeNewRecord();
    }
  }, [recordId, isEditMode]);

  // Cache yüklendiğinde edit modundaki boş name alanlarını güncelle
  useEffect(() => {
    if (mcpCacheLoaded && isEditMode) {
      const mcpConfigs = cardData.mcpConfig;
      if (!mcpConfigs) return;

      setSelectedMCPs((prev) => {
        let changed = false;
        const next = { ...prev };

        Object.entries(mcpConfigs).forEach(([mcpName, config]: [string, any]) => {
          const current = next[mcpName];
          if (current && (!current.name || current.name === "")) {
            const details = getMcpParameterDetails(config.mcpId);
            const found = details.find(
              (d) => String(d.id) === String(current.id)
            );
            if (found && found.name) {
              next[mcpName] = { ...current, name: found.name };
              changed = true;
            }
          }
        });

        return changed ? next : prev;
      });
    }
  }, [mcpCacheLoaded, isEditMode, cardData.mcpConfig, getMcpParameterDetails]);

  return {
    formData,
    selectedMCPs,
    loading: loading || mcpCacheLoading,
    setLoading,
    handleChange,
    handleMCPChange,
    loadRecord,
    resetForm,
    getMCPId,
    getMCPConfig,
    getAllMCPConfigs,
    getTableName,
    getMCPNameList,
    getMCPParameterDetails,
    getScoreValue,
    mcpCacheLoaded,
    cardData,
    isEditMode,
    error: error || mcpCacheError,
    setError: (err: { message: string; open: boolean } | null) => {
      setError(err);
      if (err) setMcpCacheError(err);
    }
  };
};
